/**
 * Dual Google/Apple OIDC CTAs shared by SignIn + Register (plan §4.2 / G20).
 * Google uses the official multicolor “G” mark; Apple uses the standard glyph.
 */
import { Apple } from 'lucide-react';
import { CUSTOMER_AUTH_PRIMARY_CTA_MAX_CLASS } from '../../../app/customerTopBarStyles.js';
import { Button } from '../../../shared/ui/Button.js';

export interface OidcSocialButtonsProps {
  readonly t: (key: string) => string;
  readonly showGoogle: boolean;
  readonly showApple: boolean;
  readonly disabled?: boolean;
  readonly onGoogle: () => void;
  readonly onApple: () => void;
  readonly dividerTestId?: string;
}

/** Official Google “G” mark (brand colors) — customary for Sign in with Google. */
function GoogleMarkIcon({ className }: { readonly className?: string }): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width={20}
      height={20}
      aria-hidden="true"
      focusable="false"
      data-testid="customer-auth-google-icon"
    >
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

export function OidcSocialButtons({
  t,
  showGoogle,
  showApple,
  disabled = false,
  onGoogle,
  onApple,
  dividerTestId = 'customer-sign-in-sso-divider',
}: OidcSocialButtonsProps): JSX.Element | null {
  if (!showGoogle && !showApple) {
    return null;
  }

  const googleLabel = t('auth.oidc.continueGoogle');
  const appleLabel = t('auth.oidc.continueApple');

  return (
    <>
      {showGoogle ? (
        <Button
          type="button"
          intent="secondary"
          size="lg"
          block
          disabled={disabled}
          onClick={onGoogle}
          aria-label={googleLabel}
          data-testid="customer-auth-google"
          className={`gap-3 font-medium whitespace-nowrap ${CUSTOMER_AUTH_PRIMARY_CTA_MAX_CLASS}`}
        >
          <GoogleMarkIcon className="shrink-0" />
          <span>{googleLabel}</span>
        </Button>
      ) : null}
      {showApple ? (
        <Button
          type="button"
          intent="secondary"
          size="lg"
          block
          disabled={disabled}
          onClick={onApple}
          aria-label={appleLabel}
          data-testid="customer-auth-apple"
          className={`gap-3 font-medium whitespace-nowrap ${CUSTOMER_AUTH_PRIMARY_CTA_MAX_CLASS}`}
        >
          <Apple
            className="size-5 shrink-0"
            strokeWidth={1.75}
            aria-hidden
            data-testid="customer-auth-apple-icon"
          />
          <span>{appleLabel}</span>
        </Button>
      ) : null}
      <p
        className="text-center text-xs uppercase tracking-wide text-[var(--color-on-surface-muted)]"
        data-testid={dividerTestId}
      >
        {t('auth.ssoDivider')}
      </p>
    </>
  );
}
